import React, { useMemo } from 'react';
import styled from 'styled-components';
import { useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { FormShell, FormGrid, FormField, FormFooter } from '../../../../../generic/ui/FormLayout';
import { AccentButton } from '../../../../../generic/ui/Button';
import ClientSelector from '../../../../../generic/ui/ClientSelector/ClientSelector';
import SellerSelector from '../../../../../generic/ui/Selectors/SellerSelector';
import TeudaTypeSelector from '../../../../../generic/ui/Selectors/TeudaTypeSelector';
import CarSelector from '../../../../../generic/ui/Selectors/CarSelector';
import WarehouseSelector from '../../../../../generic/ui/Selectors/WarehouseSelector';
import { RootState } from '../../../../../../store/reducers/rootReducer';
import { IClientsItem } from '../../../../../modules/API/Defaults/models/customers';
import { ISellerItem } from '../../../../../modules/API/Defaults/models/seller';
import { ITeudotTypeItem } from '../../../../../modules/API/Defaults/models/teudotTypes';
import { ICarItem } from '../../../../../../models/Defaults/carList';
import { IWarhouseListItem } from '../../../../../modules/API/Defaults/models/warhouseList';

type Props = {
    carList: ICarItem[] | null;
    carID: number;
    setCarID: (id: number) => void;
    sellerList: ISellerItem[] | null;
    sellerID: number;
    setSellerID: React.Dispatch<React.SetStateAction<number>>;
    teudaType: number;
    teudaTypeList: ITeudotTypeItem[] | null;
    setTeudaType: React.Dispatch<React.SetStateAction<number>>;
    selectedClient: number | null;
    setSelectedClient: (id: number, name: string) => void;
    handleClose: () => void;
    tranzitList: IWarhouseListItem[] | null;
    tranzitID: number;
    setTranzitID: React.Dispatch<React.SetStateAction<number>>;
    remark: string;
    setRemark: (value: string) => void;
};

const SettingsModsl: React.FC<Props> = ({
    sellerList,
    sellerID,
    setSellerID,
    teudaType,
    teudaTypeList,
    setTeudaType,
    selectedClient,
    setSelectedClient,
    carList,
    carID,
    setCarID,
    handleClose,
    tranzitList,
    tranzitID,
    setTranzitID,
    remark,
    setRemark,
}) => {
    const [t] = useTranslation(['teudot', 'buttons', 'knisa']);
    const clients = useSelector(
        (state: RootState): IClientsItem[] => state.defaults?.clientList?.list,
    );

    const clientValue = useMemo(
        () => clients?.find((c) => c.CustomerID === selectedClient) ?? null,
        [clients, selectedClient],
    );
    const sellerValue = useMemo(
        () => sellerList?.find((s) => s.SellerID === sellerID) ?? null,
        [sellerList, sellerID],
    );
    const teudaTypeValue = useMemo(
        () => teudaTypeList?.find((t) => t.SerialID === teudaType) ?? null,
        [teudaTypeList, teudaType],
    );
    const carValue = useMemo(
        () => carList?.find((c) => c.CarID === carID) ?? null,
        [carList, carID],
    );
    const tranzitValue = useMemo(
        () => tranzitList?.find((w) => w.WarehouseID === tranzitID) ?? null,
        [tranzitList, tranzitID],
    );

    return (
        <TallFormShell>
            <FormGrid>
                <FormField label={t('teudot_create_modal_title_client')} required fullWidth>
                    <ClientSelector
                        clients={(clients ?? []) as any}
                        value={clientValue as any}
                        onSelect={(c) =>
                            setSelectedClient(
                                +c.CustomerID,
                                String(c.Customer_Name ?? '').trim(),
                            )
                        }
                    />
                </FormField>

                <FormField label={t('teudot_create_modal_title_seller')}>
                    <SellerSelector
                        sellers={sellerList ?? []}
                        value={sellerValue}
                        onSelect={(s) => setSellerID(s ? s.SellerID : 0)}
                    />
                </FormField>

                <FormField label={t('teudot_create_modal_title_teudaType')} required>
                    <TeudaTypeSelector
                        types={teudaTypeList ?? []}
                        value={teudaTypeValue}
                        onSelect={(tp) => setTeudaType(tp ? tp.SerialID : 0)}
                    />
                </FormField>

                <FormField label={t('teudot_create_modal_title_car')}>
                    <CarSelector
                        cars={carList ?? []}
                        value={carValue}
                        onSelect={(c) => setCarID(c ? c.CarID : 0)}
                    />
                </FormField>

                <FormField label={t('knisa:knisa_create_modal_title_warehouse')}>
                    <WarehouseSelector
                        warehouses={tranzitList ?? []}
                        value={tranzitValue}
                        onSelect={(w) => setTranzitID(w ? w.WarehouseID : 0)}
                    />
                </FormField>

                <FormField label={t('knisa:knisa_create_modal_title_remark')} fullWidth>
                    <RemarkArea
                        value={remark}
                        onChange={(e) => setRemark(e.target.value)}
                        placeholder={t('knisa:knisa_create_modal_title_remark')}
                        rows={3}
                    />
                </FormField>
            </FormGrid>
            <FormFooter>
                <AccentButton
                    type='button'
                    onClick={handleClose}
                    disabled={!selectedClient || !teudaType}
                >
                    {'המשך'}
                </AccentButton>
            </FormFooter>
        </TallFormShell>
    );
};

export default SettingsModsl;

/*
 * Settings has only 5 fields so the modal collapsed too short — give it
 * a comfortable minimum height (selector dropdowns also need vertical
 * room to expand without immediately hitting the modal edge).
 */
const TallFormShell = styled(FormShell)`
    min-height: 32rem;

    @media (max-width: ${({ theme }) => theme.breakpoints.mobile}) {
        min-height: 28rem;
    }
`;

const RemarkArea = styled.textarea`
    width: 100%;
    min-height: 5rem;
    background: ${({ theme }) => theme.colors.surface};
    border: 1px solid ${({ theme }) => theme.colors.inputBorder};
    border-radius: 0.875rem;
    padding: 0.75rem 0.875rem;
    font-family: ${({ theme }) => theme.fonts.sans};
    font-size: ${({ theme }) => theme.text.lg};
    color: ${({ theme }) => theme.colors.ink};
    outline: none;
    resize: vertical;
    direction: rtl;
    transition:
        border-color ${({ theme }) => theme.motion.fast} ease,
        box-shadow ${({ theme }) => theme.motion.fast} ease;

    &::placeholder {
        color: ${({ theme }) => theme.colors.ink4};
    }

    &:hover:not(:disabled):not(:focus) {
        border-color: ${({ theme }) => theme.colors.inputBorderHover};
    }

    &:focus {
        border-color: ${({ theme }) => theme.colors.ink};
        box-shadow: ${({ theme }) => theme.shadows.focus};
    }

    @media (max-width: ${({ theme }) => theme.breakpoints.mobile}) {
        font-size: ${({ theme }) => theme.textMobile.lg};
    }
`;
